import { useState } from "react";
import produce from "immer";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  CardContent,
  CardHeader,
  Divider,
  FormHelperText,
  Grid,
  MenuItem,
  Switch,
  Typography,
} from "@material-ui/core";
import MailOutlineIcon from "@material-ui/icons/MailOutline";
import StorageIcon from "@material-ui/icons/Storage";
import SettingsIcon from "@material-ui/icons/Settings";
import DesktopWindowsIcon from "@material-ui/icons/DesktopWindows";
import SwitchField from "components/Forms/SwitchField";
import SelectField from "components/Forms/SelectField";
import { initAlerts, isTypeActive, notificationTypes } from "./utils";
import { LevelRow, StyledCard } from "./Styled";
import HelpIcon from "@material-ui/icons/Help";

import {
  DigestPeriod,
  NotificationLevel,
  NotificationSettings,
  NotificationType,
} from "@dashboard-v3/api";
import { AlertFields } from "./types";

interface Props {
  settings: NotificationSettings;
  onChange: (fields: AlertFields) => void;
}

export default function EmailAlertsConfiguration(props: Props) {
  const { t } = useTranslation("notificationSettings");
  const { settings, onChange } = props;
  const [fields, setFields] = useState<AlertFields>(() =>
    initAlerts(settings.selectors)
  );

  const update = (cb: (draft: AlertFields) => void) => {
    setFields(prev => {
      const next = produce(prev, cb);
      onChange(next);
      return next;
    });
  };

  const toggleAlertType = (type: NotificationType) => () => {
    update(draft => {
      const allActive = isTypeActive(draft[type]);
      draft[type].forEach(l => {
        l.checked = !allActive;
        if (!l.checked) l.digestPeriod = "HOUR";
      });
    });
  };

  const toggleAlertLevel =
    (type: NotificationType, level: NotificationLevel) => () => {
      update(draft => {
        const field = draft[type].find(l => l.notificationLevel === level);
        if (field) {
          field.checked = !field.checked;
          if (!field.checked) field.digestPeriod = "HOUR";
        }
      });
    };

  const changePeriod = (
    type: NotificationType,
    level: NotificationLevel,
    period: DigestPeriod
  ) => {
    update(draft => {
      const field = draft[type].find(l => l.notificationLevel === level);
      if (field) field.digestPeriod = period;
    });
  };

  return (
    <Box padding="24px">
      <Box
        marginBottom="20px"
        display="flex"
        alignItems="center"
        justifyContent="space-between"
      >
        <Box>
          <Typography style={{ fontWeight: "500" }}>
            {t("form.notifyWarnings.label")}
          </Typography>
          <FormHelperText>
            {t("form.notifyWarnings.description")}
          </FormHelperText>
        </Box>
        <Box>
          <Button
            href="https://mxhero.helpjuice.com/general-information/list-of-error-codes"
            target="_blank"
            rel="noopener noreferrer"
            color="primary"
            startIcon={<HelpIcon />}
          >
            {t("common:help")}
          </Button>
        </Box>
      </Box>
      <Grid container spacing={2}>
        {notificationTypes.map(type => {
          const config = typeConfig[type];
          const levels = fields[type];
          const active = isTypeActive(levels);

          return (
            <Grid item xs={12} md={6} key={type}>
              <StyledCard variant="outlined">
                <CardHeader
                  avatar={config.icon}
                  title={t(config.transKey)}
                  action={
                    <Switch
                      color="primary"
                      checked={active}
                      onChange={toggleAlertType(type)}
                    />
                  }
                />
                <Divider />
                <CardContent>
                  {levels.map(field => (
                    <LevelRow key={field.notificationLevel}>
                      <SwitchField
                        name={`${type}_${field.notificationLevel}`}
                        label={t(
                          `form.notificationType.${type}.${field.notificationLevel}`
                        )}
                        value={field.checked}
                        onChange={toggleAlertLevel(
                          type,
                          field.notificationLevel
                        )}
                      />
                      <Box width="9rem" marginLeft="auto">
                        <SelectField
                          name={`${type}_${field.notificationLevel}_period`}
                          options={digestPeriodOptions}
                          value={field.digestPeriod}
                          onChange={e =>
                            changePeriod(
                              type,
                              field.notificationLevel,
                              e.target.value as DigestPeriod
                            )
                          }
                          disabled={!field.checked}
                          renderOption={(opt: DigestPeriod) => (
                            <MenuItem key={opt} value={opt}>
                              {t(`form.digestPeriod.option.${opt}`) as string}
                            </MenuItem>
                          )}
                        />
                      </Box>
                    </LevelRow>
                  ))}
                </CardContent>
              </StyledCard>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
}

const digestPeriodOptions: DigestPeriod[] = ["HOUR", "DAY", "WEEK"];

const typeConfig: Record<
  NotificationType,
  { icon: React.ReactNode; transKey: string }
> = {
  MAIL: { icon: <MailOutlineIcon />, transKey: "form.mailWarnings.label" },
  STORAGE: { icon: <StorageIcon />, transKey: "form.storageWarnings.label" },
  RULE: { icon: <SettingsIcon />, transKey: "form.ruleWarnings.label" },
  PLATFORM: {
    icon: <DesktopWindowsIcon />,
    transKey: "form.platformWarnings.label",
  },
};
